import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { jetAPI } from "@/lib/api";
import { format } from "date-fns";
import { PartnerBadges, VerifiedBadgeIcon } from "@/components/PartnerBadges";
import { 
  Package, 
  Search, 
  Plane,
  MapPin,
  Calendar as CalendarIcon,
  Users,
  ArrowRight,
  Loader2,
  Star
} from "lucide-react";

// Google OAuth helper
const getGoogleAuthUrl = () => {
  const redirectUrl = window.location.origin + '/';
  return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const indianCities = [
  "Mumbai", "Delhi", "Bangalore", "Chennai", "Kolkata", "Hyderabad", 
  "Pune", "Ahmedabad", "Jaipur", "Goa", "Lucknow", "Chandigarh"
];

export default function PrivateJets() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    origin_city: "",
    destination_city: "",
    departure_date: null,
    min_seats: "",
  });

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async (filterParams = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (filterParams.origin_city && filterParams.origin_city !== "any") params.origin_city = filterParams.origin_city;
      if (filterParams.destination_city && filterParams.destination_city !== "any") params.destination_city = filterParams.destination_city;
      if (filterParams.departure_date) params.departure_date = format(filterParams.departure_date, "yyyy-MM-dd");
      if (filterParams.min_seats) params.min_seats = parseInt(filterParams.min_seats);
      
      const response = await jetAPI.getAll(params);
      setListings(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch jet listings:', error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchListings(filters);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white noise-texture">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-new.png" alt="Setuxpress" className="h-10" />
              
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/browse">
                <Button variant="ghost" className="rounded-none text-white/70 hover:text-white hover:bg-white/10" data-testid="nav-cargo-btn">
                  Cargo Space
                </Button>
              </Link>
              <Link to="/auth">
                <Button className="rounded-none btn-hover bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90" data-testid="nav-auth-btn">
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-16 min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/5778221/pexels-photo-5778221.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Private jet interior"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <p className="label-text mb-4 text-[#D4AF37]">
              Exclusive Travel
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none mb-6">
              Private Jet<br />
              <span className="luxury-text">Seat Sharing</span>
            </h1>
            <p className="text-lg text-white/70 mb-8 max-w-lg">
              Book vacant seats on private jets across India. Luxury travel at a fraction of the cost.
            </p>
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="relative z-20 -mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass p-6">
            <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label className="text-white/60 text-xs uppercase tracking-wider">From</Label>
                <Select
                  value={filters.origin_city}
                  onValueChange={(value) => setFilters({ ...filters, origin_city: value })}
                >
                  <SelectTrigger className="rounded-none bg-white/10 border-white/10 text-white" data-testid="jet-filter-origin">
                    <SelectValue placeholder="Origin City" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any City</SelectItem>
                    {indianCities.map((city) => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/60 text-xs uppercase tracking-wider">To</Label>
                <Select
                  value={filters.destination_city}
                  onValueChange={(value) => setFilters({ ...filters, destination_city: value })}
                >
                  <SelectTrigger className="rounded-none bg-white/10 border-white/10 text-white" data-testid="jet-filter-destination">
                    <SelectValue placeholder="Destination City" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any City</SelectItem>
                    {indianCities.map((city) => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/60 text-xs uppercase tracking-wider">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start rounded-none font-normal bg-white/10 border-white/10 text-white hover:bg-white/20" data-testid="jet-filter-date">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {filters.departure_date ? format(filters.departure_date, "PPP") : "Select Date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={filters.departure_date}
                      onSelect={(date) => setFilters({ ...filters, departure_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/60 text-xs uppercase tracking-wider">Seats</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="Min. seats"
                  value={filters.min_seats}
                  onChange={(e) => setFilters({ ...filters, min_seats: e.target.value })}
                  className="rounded-none bg-white/10 border-white/10 text-white placeholder:text-white/50"
                  data-testid="jet-filter-seats"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/60 text-xs uppercase tracking-wider">&nbsp;</Label>
                <Button type="submit" className="w-full rounded-none btn-hover bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90" data-testid="jet-search-btn">
                  <Search className="w-4 h-4 mr-2" />
                  Search Jets
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-20">
              <Plane className="w-16 h-16 mx-auto text-white/20 mb-4" />
              <h3 className="text-xl font-bold mb-2">No Flights Available</h3>
              <p className="text-white/60 mb-4">
                Check back later for new private jet listings.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-white/60 mb-8">
                Found <strong className="text-white">{listings.length}</strong> available flights
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {listings.map((listing, idx) => (
                  <Link 
                    key={listing.id} 
                    to={`/jet/${listing.id}`}
                    className="group glass p-6 block card-hover opacity-0 animate-fade-in-up"
                    style={{ animationDelay: `${idx * 0.1}s`, animationFillMode: 'forwards' }}
                    data-testid={`jet-card-${listing.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-[#D4AF37] flex items-center justify-center">
                        <Plane className="w-6 h-6 text-black" />
                      </div>
                      <div className="flex items-center gap-1 text-[#D4AF37]">
                        <Star className="w-4 h-4 fill-current" />
                        <span className="text-sm font-medium">Premium</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-xl font-bold mb-2">
                      <span>{listing.origin_city}</span>
                      <ArrowRight className="w-5 h-5 text-white/40" />
                      <span>{listing.destination_city}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-sm text-white/60">
                        {listing.jet_model} by {listing.partner_name}
                      </p>
                      {listing.partner_verified && <VerifiedBadgeIcon className="w-4 h-4" />}
                    </div>
                    
                    {listing.partner_badges?.length > 0 && (
                      <div className="mb-3">
                        <PartnerBadges badges={listing.partner_badges} size="sm" maxShow={2} />
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm text-white/70">
                        <CalendarIcon className="w-4 h-4" />
                        <span>{listing.departure_date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-white/70">
                        <Users className="w-4 h-4" />
                        <span>{listing.available_seats} seats</span>
                      </div>
                    </div>
                    
                    {listing.amenities && listing.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {listing.amenities.slice(0, 3).map((amenity) => (
                          <span key={amenity} className="text-xs px-2 py-1 bg-white/10 text-white/70">
                            {amenity}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <div>
                        <span className="text-3xl font-black text-[#D4AF37]">
                          {listing.price_per_seat.toLocaleString('en-IN')}
                        </span>
                        <span className="text-sm text-white/60 ml-1">INR/seat</span>
                      </div>
                      <Button size="sm" className="rounded-none bg-white/10 text-white hover:bg-white hover:text-black">
                        Book Seat
                      </Button>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-black mb-4">Own a Private Jet?</h2>
          <p className="text-white/60 mb-6 max-w-lg mx-auto">
            List your vacant seats on Setuxpress and earn from every flight. Join elite jet owners across India.
          </p>
          <a href={getGoogleAuthUrl()}>
            <Button className="rounded-none btn-hover bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90" data-testid="jet-owner-cta">
              List Your Jet
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-new.png" alt="Setuxpress" className="h-10" />
              <span className="font-black text-xl tracking-tight">Setuxpress</span>
            </div>
            <p className="text-sm text-white/40">
              &copy; 2024 Setuxpress Hub. Private Jet Seat Sharing Platform.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
